<template>
  <base-dialog :value="isVisibleValue"
               hideBtn
               title="Install the chat on your site"
               variant="base"
               @cancel="isVisibleValue = false"

  >
    <template #prompt-input>
      <div class="text-sm text-gray-dark">
        Insert these code snippets before their respective closing tags on every page you want
        the chat to appear.
      </div>

      <BaseToggleGroup class="mt-4" :tabs="TAB_OPTIONS" v-model="activeTab" />

      <div class="mt-4">
        <SettingsBotSnippetJavaScript v-if="activeTab === 'js'"
                                      :head-code-snippet-text="HEAD_CODE_SNIPPET_TEXT"
                                      :body-code-snippet-text="bodyCodeSnippetText"
                                      @go-to-theme="goToTheme"
        />
        <SettingsBotSnippetGoogleTagManager v-else
                                      :widget-url="WIDGET_URL"
                                      :code-snippet-text="googleTagManagerCodeSnippetText"
                                      @go-to-theme="goToTheme"
        />
      </div>
    </template>
  </base-dialog>
</template>

<script>
import { ROUTE_NAMES } from '@/router'
import SettingsBotSnippetJavaScript from './SettingsBotSnippetJavaScript.vue'
import SettingsBotSnippetGoogleTagManager from './SettingsBotSnippetGoogleTagManager.vue'

const WIDGET_URL = 'https://chat-widget.static-amio.com/static/amio-webchat-loader.min.js'
const HEAD_CODE_SNIPPET_TEXT = `<script src="${WIDGET_URL}" type="text/javascript"></` + 'script>'
const TAB_OPTIONS = Object.freeze([
  { key: 'js', label: 'JavaScript' },
  { key: 'gtm', label: 'Google Tag Manager' }
])

export default {
  name: 'SettingsBotSnippet',

  components: { SettingsBotSnippetJavaScript, SettingsBotSnippetGoogleTagManager },

  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    bot: {
      type: Object,
      required: true
    },
    selectedChannel: {
      type: Object,
      default: () => {}
    },
    selectedTheme: {
      type: Object,
      default: null
    },
    selectedLangConfig: {
      type: Object,
      default: () => {}
    },
    selectedLanguage: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      isHeadCodeSnippetCopied: false,
      isBodyCodeSnippetCopied: false,
      ROUTE_NAMES,
      HEAD_CODE_SNIPPET_TEXT,
      WIDGET_URL,
      TAB_OPTIONS,
      activeTab: 'js'
    }
  },

  emits: ['update:is-visible'],

  computed: {
    isVisibleValue: {
      get() {
        return this.isVisible
      },
      set(value) {
        this.$emit('update:is-visible', value)
      }
    },

    bodyCodeSnippetText() {
      return `<script type="text/javascript">
  window.amioWebchat.init({
    // All options are listed at https://docs.amio.io/docs/amio-chat-api-methods#init
    channelId: '${this.selectedChannel.id}',
    theme: 'amio',
    lang: '${this.selectedLanguage}',
    startOpened: true,
    wideButton: ${!!this.selectedLangConfig.buttonTitle},
    langOverride: ${this.getFormattedLangOverride()},
    themeOverride: ${this.getFormattedThemeOverride()}
  })
</` + 'script>'
    },

    googleTagManagerCodeSnippetText() {
      return `<script>
  (function(d, s, id) {
    if (d.getElementById(id)) return;
    var js = d.createElement(s);
    js.async = true;
    js.src = "https://chat-widget.static-amio.com/static/amio-webchat-loader.min.js";
    js.id = id;
    js.onload = function () {
      window.amioWebchat.init({
        // All options are listed at https://docs.amio.io/docs/amio-chat-api-methods#init
        channelId: '${this.selectedChannel.id}',
        theme: 'amio',
        lang: '${this.selectedLanguage}',
        startOpened: true,
        wideButton: ${!!this.selectedLangConfig.buttonTitle},
        langOverride: ${this.getFormattedLangOverride(8)},
        themeOverride: ${this.getFormattedThemeOverride(8)}
      });
    };
    d.head.appendChild(js);
  }) (document, 'script', 'amio-webchat');
</` + 'script>'
    }
  },

  methods: {
    getFormattedLangOverride(spaces = 4) {
      const langOverride = this.selectedLangConfig || {}
      return JSON.stringify(langOverride, null, 2)
        .split('\n')
        .map((line, index) => index === 0 ? line : `${' '.repeat(spaces)}${line}`)
        .join('\n')
    },
    getFormattedThemeOverride(spaces = 4) {
      const themeOverride = (this.selectedTheme && this.selectedTheme.themeConfig) || {}
      return JSON.stringify(themeOverride, null, 2)
        .split('\n')
        .map((line, index) => index === 0 ? line : `${' '.repeat(spaces)}${line}`)
        .join('\n')
    },
    async goToTheme() {
      this.isVisibleValue = false
      await this.$nextTick()
      this.$router.push({
        name: ROUTE_NAMES.botTheme,
        params: { chatThemeId: this.selectedTheme.id }
      })
    }
  }
}
</script>
